import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Phone, Lock, Mail, User, ArrowLeft, Gift } from "lucide-react";
import { useFirebaseOTP } from "@/hooks/useFirebaseOTP";
import { apiError, normalizeMobile } from "@/lib/apiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [idToken, setIdToken] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    otp: "",
    referred_by: searchParams.get("ref") || "",
  });

  const otp = useFirebaseOTP();
  const firebaseConfigured = !!process.env.REACT_APP_FIREBASE_API_KEY;
  const [otpFailed, setOtpFailed] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendOTP = async () => {
    if (normalizeMobile(formData.mobile).length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!firebaseConfigured) {
      setStep(3);
      return;
    }
    const ok = await otp.sendOTP(formData.mobile);
    if (ok) setStep(2);
    else setOtpFailed(true);
  };

  const verifyOTP = async () => {
    const token = await otp.verifyOTP(formData.otp);
    if (token) {
      setIdToken(token);
      toast.success("Mobile verified!");
      setStep(3);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const body = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        password: formData.password,
        referred_by: formData.referred_by.trim() || null,
      };
      const res = idToken
        ? await axios.post(`${API}/auth/firebase`, { ...body, id_token: idToken, purpose: "register_user" })
        : await axios.post(`${API}/auth/register/user`, { ...body, mobile: normalizeMobile(formData.mobile) });
      login(res.data.user, res.data.token);
      toast.success("Registration successful!");
      navigate("/dashboard");
    } catch (error) {
      const msg = apiError(error, "Registration failed");
      toast.error(msg.includes("already registered") ? "This mobile is already registered. Please sign in." : msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="M Ventures" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Create Account</h1>
          <p className="text-gray-500">Start earning cashback today</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`w-10 h-1 rounded-full transition-colors ${
                step >= s ? "bg-primary-500" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      placeholder="Enter 10-digit mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="pl-10"
                      maxLength={16}
                      data-testid="register-mobile-input"
                    />
                  </div>
                </div>

                {formData.referred_by && (
                  <div className="p-3 bg-primary-50 rounded-xl flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary-600" />
                    <span className="text-sm text-primary-700">
                      Referral code: <strong>{formData.referred_by}</strong>
                    </span>
                  </div>
                )}

                <div>
                  <Label htmlFor="referred_by">Referral Code (Optional)</Label>
                  <Input
                    id="referred_by"
                    name="referred_by"
                    placeholder="Enter referral code"
                    value={formData.referred_by}
                    onChange={handleChange}
                    data-testid="register-referral-input"
                  />
                </div>

                <Button
                  onClick={sendOTP}
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
                  disabled={otp.sending || !formData.mobile}
                  data-testid="register-send-otp-btn"
                >
                  {otp.sending ? "Sending..." : firebaseConfigured ? "Send OTP" : "Continue"}
                </Button>

                {otpFailed && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm" data-testid="register-otp-failed-box">
                    <p className="text-amber-800 mb-2">
                      We couldn't send the OTP right now. You can continue sign-up and verify your mobile later.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => setStep(3)}
                      data-testid="register-skip-otp-btn"
                    >
                      Continue without OTP
                    </Button>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-gray-500">
                    Enter OTP sent to <strong>{formData.mobile}</strong>
                  </p>
                </div>

                <div>
                  <Label htmlFor="otp">OTP</Label>
                  <Input
                    id="otp"
                    name="otp"
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={formData.otp}
                    onChange={handleChange}
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                    data-testid="register-otp-input"
                  />
                </div>

                <Button
                  onClick={verifyOTP}
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
                  disabled={otp.verifying || formData.otp.length !== 6}
                  data-testid="register-verify-otp-btn"
                >
                  {otp.verifying ? "Verifying..." : "Verify OTP"}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-center text-primary-600 text-sm"
                >
                  Change mobile number
                </button>
              </div>
            )}

            {step === 3 && (
              <form onSubmit={handleRegister} className="space-y-4">
                {idToken ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid="register-mobile-verified-badge">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1 text-sm">
                    <span className="text-gray-600">+91 {formData.mobile}</span>
                    <span className="ml-2 text-green-700 font-semibold">Verified</span>
                  </div>
                </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm" data-testid="register-mobile-unverified-badge">
                    <span className="text-gray-600">+91 {normalizeMobile(formData.mobile)}</span>
                    <button type="button" className="text-primary-600" onClick={() => setStep(1)}>Change</button>
                  </div>
                )}

                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email (Optional)</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create password"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            )}

            <p className="text-center mt-6 text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="text-primary-600 font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Invisible reCAPTCHA container for Firebase */}
        <div id="recaptcha-container"></div>

        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
